import { useState, useEffect } from "react";

const CHECK_SDK_URL = "https://cdn.checkhq.com/component-initialize.js";
const CHECK_SDK_SCRIPT_ID = "check-component-sdk";

let sdkLoadingPromise: Promise<void> | null = null;
let sdkLoaded = false;

export type UseCheckSDKResult = {
  sdkLoaded: boolean;
  sdkError: Error | null;
};

export const useCheckSDK = (): UseCheckSDKResult => {
  const [loaded, setLoaded] = useState(sdkLoaded);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const loadSDK = async () => {
      if (sdkLoaded) {
        setLoaded(true);
        return;
      }

      if (sdkLoadingPromise) {
        try {
          await sdkLoadingPromise;
          setLoaded(true);
        } catch (err) {
          setError(err instanceof Error ? err : new Error(String(err)));
        }
        return;
      }

      sdkLoadingPromise = new Promise<void>((resolve, reject) => {
        const existingScript = document.getElementById(CHECK_SDK_SCRIPT_ID);
        
        if (existingScript) {
          if (window.CheckComponent) {
            sdkLoaded = true;
            resolve();
            return;
          }
          existingScript.remove();
        }

        console.log("[useCheckSDK] Loading CheckHQ SDK from:", CHECK_SDK_URL);

        const script = document.createElement("script");
        script.id = CHECK_SDK_SCRIPT_ID;
        script.src = CHECK_SDK_URL;
        script.async = true;

        script.onload = () => {
          console.log("[useCheckSDK] CheckHQ SDK loaded successfully");
          if (window.CheckComponent) {
            sdkLoaded = true;
            resolve();
          } else {
            const err = new Error("CheckHQ SDK loaded but CheckComponent not found on window");
            console.error("[useCheckSDK]", err);
            reject(err);
          }
        };

        script.onerror = () => {
          const err = new Error("Failed to load CheckHQ SDK");
          console.error("[useCheckSDK]", err);
          reject(err);
        };

        document.body.appendChild(script);
      });

      try {
        await sdkLoadingPromise;
        setLoaded(true);
      } catch (err) {
        console.error("[useCheckSDK] Error loading SDK:", err);
        setError(err instanceof Error ? err : new Error(String(err)));
        sdkLoadingPromise = null;
      }
    };

    loadSDK();
  }, []);

  return { sdkLoaded: loaded, sdkError: error };
};


